import React, { useEffect, useState } from 'react';

import { Button, Modal } from '~components';
import Switch from '~components/form-fields/inputs/Switch';

import { FormTemplate } from '~forms/types';

interface ComponentProps {
  isOpen: boolean;
  setIsOpen: (val: boolean) => void;
  template: FormTemplate;
  canEdit: boolean;
  onSave: (settings: { title: string; description: string; allow_care_worker_fill: boolean }) => Promise<void>;
}

// Template-level settings (title, description, care-worker fill flag) edited in one place, opened from the
// builder toolbar's cog. These are template attributes — not form fields — so they live here rather than on
// the canvas or the field-properties panel.
const FormSettingsModal: React.FC<ComponentProps> = ({ isOpen, setIsOpen, template, canEdit, onSave }) => {
  const [title, setTitle] = useState(template.title);
  const [description, setDescription] = useState(template.description ?? '');
  const [allowCareWorkerFill, setAllowCareWorkerFill] = useState(template.allow_care_worker_fill);

  // Re-seed local state whenever the modal (re)opens or the underlying template changes, so a cancelled
  // edit never leaks into the next open and external updates (autosave/refetch) stay reflected.
  useEffect(() => {
    if (isOpen) {
      setTitle(template.title);
      setDescription(template.description ?? '');
      setAllowCareWorkerFill(template.allow_care_worker_fill);
    }
  }, [isOpen, template.title, template.description, template.allow_care_worker_fill]);

  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave({ title: title.trim(), description: description.trim(), allow_care_worker_fill: allowCareWorkerFill });
      setIsOpen(false);
    } catch {
      // onSave surfaces its own error toast and re-throws to signal failure; swallow it here so the
      // modal stays open (setIsOpen(false) is skipped) without leaving an unhandled promise rejection.
    } finally {
      setIsSaving(false);
    }
  };

  const saveDisabled = !canEdit || !title.trim() || isSaving;

  const footer = canEdit ? (
    <>
      <Button color="info" outline onClick={() => setIsOpen(false)} dataCy="form-settings-cancel-btn">
        Cancel
      </Button>
      <Button color="success" onClick={handleSave} disabled={saveDisabled} dataCy="form-settings-save-btn">
        {isSaving ? 'Saving…' : 'Save'}
      </Button>
    </>
  ) : undefined;

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      size="lg"
      header="Form Settings"
      className="form-settings-modal feature"
      data-cy="form-settings-modal"
      footer={footer}
    >
      <div className="mb-3">
        <label htmlFor="form-settings-title" className="form-label">
          Title
        </label>
        <input
          id="form-settings-title"
          type="text"
          className="form-control"
          value={title}
          disabled={!canEdit}
          onChange={(e) => setTitle(e.target.value)}
          data-cy="form-settings-title-input"
        />
      </div>
      <div className="mb-3">
        <label htmlFor="form-settings-description" className="form-label">
          Description
        </label>
        <textarea
          id="form-settings-description"
          className="form-control"
          value={description}
          disabled={!canEdit}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Optional description"
          data-cy="form-settings-description-input"
        />
      </div>
      <Switch
        type="switch"
        id="form-settings-care-worker-fill"
        name="form-settings-care-worker-fill"
        value={allowCareWorkerFill}
        disabled={!canEdit}
        onChange={() => setAllowCareWorkerFill((prev) => !prev)}
        labels={['Care workers cannot fill', 'Care workers can fill']}
      />
    </Modal>
  );
};

export default FormSettingsModal;
